import lodashMap from 'lodash/map';

import { IPagination } from '@lib/utils';
import { IVehicleListServerResponse, IVehicleServerResponse } from '@app/admin/vehicles/list.service';
import {
  IEntitySearchResults,
  IServerDriversResponse,
  IRadioListConfig,
  IBulkEntitySelectionConfig,
  IEntityForBulkSelection,
  IPersonalizationItem,
} from './interfaces';

import { PhxSelectOption } from 'phx-core/lib/select/select.types';

import {
  CARD_ENTITIES,
  CARD_ENTITIES_TITLES,
  CARD_ENTITIES_PLURAL_TITLES,
} from '@app/admin/fleet-card/main.constants';

export const DEFAULT_COUNTRY = 'US';

export const CARD_TYPE_OPTIONS: IRadioListConfig[] = [
  {
    label: $localize`Motive Card`,
    value: 'generic',
    subtitle: $localize`Fleet cards with no pre-built spending restrictions.`,
  },
  {
    label: $localize`Motive Fuel-Only Card`,
    value: 'fuel_only',
    subtitle: $localize`Spend restrictions for non-fuel items are built in.`,
  },
];

export const PERSONALIZATION_OPTIONS: IRadioListConfig[] = [
  {
    label: $localize`No Customization`,
    value: false,
    subtitle: $localize`Motive will auto-generate a unique ID for each card.`,
  },
  {
    label: $localize`Custom Card ID`,
    value: true,
    subtitle: $localize`Create a unique ID with a name or vehicle ID.`,
  },
];

export enum CARD_PERSONALIZATIONS {
  custom = 'true',
  standard = 'false',
}

export const POST_APPLICATION_FORM_CARD_STYLE_PERSONALIZATION_OPTIONS: IRadioListConfig[] = [
  {
    isRecommended: true,
    label: $localize`Standard Card ID`,
    value: CARD_PERSONALIZATIONS.standard,
    subtitle: $localize`Card IDs are automatically generated by Motive, allowing for easy card reassignment.`,
  },
  {
    isRecommended: false,
    label: $localize`Custom Card ID`,
    value: CARD_PERSONALIZATIONS.custom,
    subtitle: $localize`Choose a unique Card ID to appear directly above the company name.`,
  },
];

export const FIRST_TIME_ORDER_REASON: PhxSelectOption = {
  label: $localize`First time ordering`,
  value: 'first_cards_issued',
};

export const ORDER_REASONS: PhxSelectOption[] = [
  {
    label: $localize`New driver(s)/vehicle(s)`,
    value: 'new_drivers_vehicles',
  },
  {
    label: $localize`Lost/stolen card(s)`,
    value: 'lost_stolen_cards',
  },
  {
    label: $localize`Damaged card(s)`,
    value: 'damaged_cards',
  },
  {
    label: $localize`Card(s) not received`,
    value: 'cards_not_received',
  },
  {
    label: $localize`Card(s) incorrectly deactivated`,
    value: 'cards_incorrectly_deactivated',
  },
  {
    label: $localize`Fraud occurred`,
    value: 'fraud_occurred',
  },
  {
    label: $localize`Other`,
    value: 'other',
  },
];

export const CARD_ORDER_REASONS = {
  NEW_DRIVERS_VEHICLES: 'new_drivers_vehicles',
  LOST_STOLEN_CARDS: 'lost_stolen_cards',
  DAMAGED_CARDS: 'damaged_cards',
  CARDS_NOT_RECEIVED: 'cards_not_received',
  CARDS_INCORRECTLY_DEACTIVATED: 'cards_incorrectly_deactivated',
  FRAUD_OCCURRED: 'fraud_occurred',
  OTHER: 'other',
};

export enum InitialRequirementsFormKeys {
  cardType = 'card_product_type',
  quantity = 'number_of_cards',
  personalization = 'personalization',
  reason = 'reason',
}

export enum PersonalizationFormKeys {
  personalizationItems = 'personalization_items',
  documentId = 'company_card_document_id',
}

export enum ShippingAddressFormKeys {
  recipientName = 'name',
  recipientPhoneNumber = 'mobile',
  id = 'id',
  address1 = 'address_line1',
  address2 = 'address_line2',
  city = 'city',
  state = 'state',
  country = 'country',
  postal_code = 'postal_code',
  proofs = 'address_proofs',
  expediteShipping = 'expedite_shipping',
}

export const ShippingAddressFormLabels = {
  recipientName: $localize`Name`,
  recipientPhoneNumber: $localize`Phone number`,
  address1: $localize`Street Info`,
  city: $localize`City`,
  state: $localize`State`,
  postal_code: $localize`Zip code`,
};

export const InitialRequirementsFormLabels = {
  quantity: $localize`Quantity`,
};

export enum PersonalizationItemKeys {
  name = 'name',
  cardType = 'card_type',
  userId = 'user_id',
  vehicleId = 'vehicle_id',
  nameOnCard = 'name_on_card',
  entity = 'entity',
  isValidated = 'is_validated',
  lastValidatedValue = 'last_validated_value',
  cardIdSuggestions = 'card_id_suggestions',
}

export enum CheckoutFormGroupKeys {
  initialRequirementsForm = 'initial-requirements-form',
  bulkEntitySelectionForm = 'bulk-entity-selection-form',
  personalizationForm = 'personalization-form',
  shippingAddressForm = 'shipping-address-form',
  orderReview = 'order-review',
}

export const VALIDATE_ASSIGNEE_ERROR_KEYS = {
  ACTIVE_ORDER: 'ACTIVE_ORDER',
  UNACTIVATED_CARD: 'UNACTIVATED_CARD',
  DUPLICATE_NAME_ON_CARD: 'DUPLICATE_NAME_ON_CARD',
  ASSIGNEE_NOT_SELECTED: 'ASSIGNEE_NOT_SELECTED',
};

export const VALIDATE_ASSIGNEE_ERROR_MAP = new Map<string, string>([
  [
    VALIDATE_ASSIGNEE_ERROR_KEYS.ACTIVE_ORDER,
    $localize`Assignment not possible; previous order in-progress for assignee.`,
  ],
  [
    VALIDATE_ASSIGNEE_ERROR_KEYS.UNACTIVATED_CARD,
    $localize`Assignment not possible; assignee has existing card awaiting activation.`,
  ],
  [
    VALIDATE_ASSIGNEE_ERROR_KEYS.DUPLICATE_NAME_ON_CARD,
    $localize`Card ID duplicates an existing entry`,
  ],
  [
    VALIDATE_ASSIGNEE_ERROR_KEYS.ASSIGNEE_NOT_SELECTED,
    $localize`Please select a valid assignee.`,
  ],
]);

export const BULK_ENTITY_VALIDATE_ASSIGNEE_ERROR_MAP = new Map<string, string>([
  [
    VALIDATE_ASSIGNEE_ERROR_KEYS.ACTIVE_ORDER,
    $localize`Card order in progress`,
  ],
  [
    VALIDATE_ASSIGNEE_ERROR_KEYS.UNACTIVATED_CARD,
    $localize`Assignee has a card awaiting activation`,
  ],
]);

export enum AUTOFILL_OPTIONS {
  driver_name = 'driver_name',
  vehicle_id = 'vehicle_number',
  license_plate = 'license_plate',
  user_name = 'user_name',
  custom = 'custom',
}

export const BULK_ENTITY_SELECTION_CONFIG: IBulkEntitySelectionConfig[] = [
  {
    label: CARD_ENTITIES_TITLES[CARD_ENTITIES.DRIVER],
    labelPlural: CARD_ENTITIES_PLURAL_TITLES[CARD_ENTITIES.DRIVER],
    key: CARD_ENTITIES.DRIVER,
    autoFillOptions: [
      { label: $localize`Driver name`, value: AUTOFILL_OPTIONS.user_name },
      { label: $localize`Nothing (leave blank)`, value: AUTOFILL_OPTIONS.custom },
    ],
    selectedAutoFillOption: AUTOFILL_OPTIONS.user_name,
    currentPage: 1,
    api: (companyId?: number) => `companies/${companyId}/connections`,
    identifiersApi: (companyId?: number) => `companies/${companyId}/connections/identifiers`,
    params: (search: string, groupIds: string[], pageNumber: number) => {
      const params = new Map<string, string | number | string[]>();
      params.set('user_role', 'driver');
      params.set('per_page', 10);
      if (search) params.set('name', search);
      params.set('group_ids', groupIds);
      params.set('sort_field', 'full_name');
      params.set('sort_direction', 'asc');
      params.set('statuses', ['active']);
      params.set('page_no', pageNumber);
      return params;
    },
    identifiersParams: (searchValue: string, groupIds: string[]) => {
      const params = new Map<string, string | number | string[] | boolean>();
      if (searchValue) params.set('name', searchValue);
      params.set('group_ids', groupIds);
      params.set('all_drivers', false);
      params.set('status', 'active');
      params.set('user_role', 'driver');
      return params;
    },
    apiResponseTransformer: (response: IServerDriversResponse): IEntitySearchResults => {
      const entities: IEntityForBulkSelection[] = lodashMap(
        response.company_connections, 'company_connection',
      ).map((connection): IEntityForBulkSelection => {
        const user = connection.user;
        return {
          name: user?.first_name + ' ' + user?.last_name,
          id: user.driver_company_id,
          groups: user.groups.map(
            group => { return { name: group.group.name, id: group.group.id.toString() }; },
          ),
          isDisabled: false,
          disabledReason: '',
          companyId: user.id,
          type: CARD_ENTITIES.DRIVER,
        };
      });
      const pagination: IPagination = {
        perPage: response.per_page,
        totalItemsInLastResponse: response.company_connections.length,
        pageNo: response.page_no,
        totalItems: response.total,
        noOfPages: Math.ceil(response.total / response.per_page),
      };
      return { entities, pagination };
    },
    mapToPersonalizationItem: (entity: IEntityForBulkSelection): IPersonalizationItem => {
      return {
        cardType: CARD_ENTITIES.DRIVER as 'driver',
        userId: entity.companyId,
        nameOnCard: entity.name,
        name: entity.name,
      };
    },
    entities: [],
    selectedEntities: {},
    hasFetchedData: false,
    addNewEntityRoute: '/admin/drivers/all/new',
    shouldShowFiltersUnAffectiveMessage: false,
  },
  {
    label: CARD_ENTITIES_TITLES[CARD_ENTITIES.VEHICLE],
    labelPlural: CARD_ENTITIES_PLURAL_TITLES[CARD_ENTITIES.VEHICLE],
    key: CARD_ENTITIES.VEHICLE,
    autoFillOptions: [
      { label: $localize`Vehicle ID`, value: AUTOFILL_OPTIONS.vehicle_id },
      { label: $localize`License Plate (if available)`, value: AUTOFILL_OPTIONS.license_plate },
      { label: $localize`Nothing (leave blank)`, value: AUTOFILL_OPTIONS.custom },
    ],
    selectedAutoFillOption: AUTOFILL_OPTIONS.vehicle_id,
    currentPage: 1,
    api: (companyId?: number) => 'vehicles/admin',
    identifiersApi: (companyId?: number) => `vehicles/admin/identifiers`,
    params: (searchValue: string, groupIds: string[], pageNumber: number) => {
      const params = new Map<string, string | number | string[]>();
      if (searchValue) params.set('number', searchValue);
      params.set('per_page', 10);
      params.set('group_ids', groupIds);
      params.set('sort_field', 'number');
      params.set('sort_direction', 'asc');
      params.set('status', 'active');
      params.set('page_no', pageNumber);
      return params;
    },
    identifiersParams: (searchValue: string, groupIds: string[]) => {
      const params = new Map<string, string | number | string[] | boolean>();
      if (searchValue) params.set('number', searchValue);
      params.set('group_ids', groupIds);
      params.set('all_vehicles', false);
      params.set('status', 'active');
      return params;
    },
    apiResponseTransformer: (response: IVehicleListServerResponse): IEntitySearchResults => {
      const entities: IEntityForBulkSelection[] = lodashMap(
        response.vehicles, 'vehicle',
      ).map((vehicle: IVehicleServerResponse): IEntityForBulkSelection => {
        return {
          name: vehicle.number,
          id: vehicle.make + ' ' + vehicle.license_plate_number,
          groups: vehicle.groups.map(
            group => { return { name: group.group.name, id: group.group.id.toString() }; },
          ),
          isDisabled: false,
          disabledReason: '',
          companyId: vehicle.id.toString(),
          type: CARD_ENTITIES.VEHICLE,
        };
      });
      const pagination: IPagination = {
        perPage: response.per_page,
        totalItemsInLastResponse: response.vehicles.length,
        pageNo: response.page_no,
        totalItems: response.total,
        noOfPages: Math.ceil(response.total / response.per_page),
      };
      return { entities, pagination };
    },
    mapToPersonalizationItem: (entity: IEntityForBulkSelection): IPersonalizationItem => {
      return {
        cardType: CARD_ENTITIES.VEHICLE as 'vehicle',
        vehicleId: entity.companyId,
        nameOnCard: entity.name,
        name: entity.name,
      };
    },
    entities: [],
    selectedEntities: {},
    hasFetchedData: false,
    addNewEntityRoute: '/admin/vehicles/new',
    shouldShowFiltersUnAffectiveMessage: false,
  },
  {
    label: CARD_ENTITIES_TITLES[CARD_ENTITIES.FLEET_USER],
    labelPlural: CARD_ENTITIES_PLURAL_TITLES[CARD_ENTITIES.FLEET_USER],
    key: CARD_ENTITIES.FLEET_USER,
    autoFillOptions: [
      { label: $localize`Fleet user name`, value: AUTOFILL_OPTIONS.user_name },
      { label: $localize`Nothing (leave blank)`, value: AUTOFILL_OPTIONS.custom },
    ],
    selectedAutoFillOption: AUTOFILL_OPTIONS.user_name,
    currentPage: 1,
    identifiersApi: (companyId?: number) => `companies/${companyId}/connections/identifiers`,
    api: (companyId?: number) => `companies/${companyId}/connections`,
    params: (searchValue: string, groupIds: string[], pageNumber: number) => {
      const params = new Map<string, string | number | string[]>();
      if (searchValue) params.set('number', searchValue);
      params.set('per_page', 10);
      params.set('sort_field', 'full_name');
      params.set('sort_direction', 'asc');
      params.set('statuses', ['active']);
      params.set('page_no', pageNumber);
      params.set('user_role', 'dispatcher');
      return params;
    },
    identifiersParams: (searchValue: string, groupIds: string[]) => {
      const params = new Map<string, string | number | string[] | boolean>();
      if (searchValue) params.set('number', searchValue);
      params.set('status', 'active');
      params.set('user_role', 'dispatcher');
      params.set('group_ids', groupIds);
      params.set('all_dispatchers', false);
      return params;
    },
    apiResponseTransformer: (response: any): IEntitySearchResults => {
      const entities: IEntityForBulkSelection[] = lodashMap(
        response.company_connections, 'company_connection',
      ).map((connection): IEntityForBulkSelection => {
        const user = connection.user;
        return {
          name: user?.first_name + ' ' + user?.last_name,
          email: user?.email,
          id: user.id,
          groups: [],
          isDisabled: false,
          disabledReason: '',
          companyId: user.id,
          type: CARD_ENTITIES.FLEET_USER,
        };
      });
      const pagination: IPagination = {
        perPage: response.per_page,
        totalItemsInLastResponse: response.company_connections.length,
        pageNo: response.page_no,
        totalItems: response.total,
        noOfPages: Math.ceil(response.total / response.per_page),
      };
      return { entities, pagination };
    },
    mapToPersonalizationItem: (entity: IEntityForBulkSelection): IPersonalizationItem => {
      return {
        cardType: CARD_ENTITIES.FLEET_USER as 'fleet_user',
        userId: entity.companyId,
        nameOnCard: entity.name,
        name: entity.name,
      };
    },
    entities: [],
    selectedEntities: {},
    hasFetchedData: false,
    addNewEntityRoute: '/admin/fleet-users/new',
    shouldShowFiltersUnAffectiveMessage: true,
  },
];

export const HAD_ERRORS_KEY: string = 'hadErrors';

export const CARD_ID_MAX_LENGTH: number = 21;
export const CARD_ID_MAX_LENGTH_FOR_VEHICLES: number = 10;